import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Box, HStack, IconButton, Tooltip } from '@chakra-ui/react';
import {
  MdArrowDownward,
  MdArrowUpward,
  MdClose,
  MdContentCopy,
} from 'react-icons/md';
import { clientToCanvas, normalizeRect, snapArrowEnd, applyRectResize } from '../utils/geometry';
import { compositeToCanvas, drawElements } from '../utils/draw';
import { hitTest } from '../utils/hitTest';
import { getElementBounds, transformElementToBounds } from '../utils/elementBounds';
import { newId } from '../utils/id';

const CURSOR = {
  select: 'default',
  crop: 'crosshair',
  rect: 'crosshair',
  arrow: 'crosshair',
  text: 'text',
  blur: 'crosshair',
  pen: 'crosshair',
};

function textFontSizeForImage(nw) {
  return Math.max(18, Math.min(48, Math.round(24 * (nw / 1200))));
}

const AnnotatorCanvas = forwardRef(function AnnotatorCanvas(
  {
    image,
    elements,
    setElements,
    setElementsDirect,
    beginGesture,
    endGesture,
    tool,
    color,
    strokeWidth,
    selectedId,
    setSelectedId,
    editingTextId,
    setEditingTextId,
    quickBlur,
    setTool,
    onDeleteSelected,
    onDuplicateSelected,
    onBringForward,
    onSendBackward,
    onApplyCrop,
  },
  ref
) {
  const baseRef = useRef(null);
  const overlayRef = useRef(null);
  const elementsRef = useRef(elements);
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);

  useImperativeHandle(
    ref,
    () => ({
      toCompositeCanvas: () => {
        const base = baseRef.current;
        if (!base || !image) return null;
        return compositeToCanvas(base, elementsRef.current);
      },
    }),
    [image]
  );

  const dragRef = useRef(null);
  const draftRef = useRef(null);
  const [draft, setDraft] = useState(null);
  const [localEditText, setLocalEditText] = useState('');
  const textInputRef = useRef(null);
  const editingIdRef = useRef(null);
  const localTextRef = useRef('');

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    editingIdRef.current = editingTextId;
  }, [editingTextId]);

  useEffect(() => {
    localTextRef.current = localEditText;
  }, [localEditText]);

  const redraw = useCallback(() => {
    const overlay = overlayRef.current;
    const base = baseRef.current;
    if (!overlay || !base || !image) return;
    const ctx = overlay.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, overlay.width, overlay.height);
    drawElements(ctx, base, elements, {
      selectedId,
      draft,
      editingTextId,
    });
  }, [elements, selectedId, draft, image, editingTextId]);

  useEffect(() => {
    const base = baseRef.current;
    const overlay = overlayRef.current;
    if (!base || !overlay || !image) return;
    const w = image.naturalWidth;
    const h = image.naturalHeight;
    base.width = w;
    base.height = h;
    overlay.width = w;
    overlay.height = h;
    const ctx = base.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, w, h);
    ctx.drawImage(image, 0, 0);
  }, [image]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  useEffect(() => {
    if (tool !== 'crop') {
      setDraft((cur) => (cur?.type === 'crop' ? null : cur));
    }
  }, [tool]);

  const elementsForTextSyncRef = useRef(elements);
  elementsForTextSyncRef.current = elements;

  useEffect(() => {
    if (!editingTextId) return;
    const el = elementsForTextSyncRef.current.find(
      (e) => e.id === editingTextId && e.type === 'text'
    );
    if (el) setLocalEditText(el.text ?? '');
  }, [editingTextId]);

  const commitDraft = useCallback(
    (d) => {
      if (!d) return;
      if (d.type === 'rect' && d.w > 2 && d.h > 2) {
        setElements((els) => [
          ...els,
          {
            type: 'rect',
            id: newId(),
            x: d.x,
            y: d.y,
            w: d.w,
            h: d.h,
            color: d.color,
            strokeWidth: d.strokeWidth,
          },
        ]);
      } else if (d.type === 'arrow') {
        const len = Math.hypot(d.x2 - d.x1, d.y2 - d.y1);
        if (len > 4) {
          setElements((els) => [
            ...els,
            {
              type: 'arrow',
              id: newId(),
              x1: d.x1,
              y1: d.y1,
              x2: d.x2,
              y2: d.y2,
              color: d.color,
              strokeWidth: d.strokeWidth,
            },
          ]);
        }
      } else if (d.type === 'blur' && d.w > 4 && d.h > 4) {
        setElements((els) => [
          ...els,
          {
            type: 'blur',
            id: newId(),
            x: d.x,
            y: d.y,
            w: d.w,
            h: d.h,
            blockSize: 12,
          },
        ]);
        if (!quickBlur) setTool('select');
      } else if (d.type === 'path' && d.points && d.points.length > 1) {
        setElements((els) => [
          ...els,
          {
            type: 'path',
            id: newId(),
            points: d.points,
            color: d.color,
            strokeWidth: d.strokeWidth,
          },
        ]);
      } else if (d.type === 'crop' && d.w >= 8 && d.h >= 8) {
        onApplyCrop?.(d, elementsRef.current);
      }
      setDraft(null);
    },
    [setElements, quickBlur, setTool, onApplyCrop]
  );

  const commitTextEditing = useCallback(() => {
    const id = editingIdRef.current;
    if (!id) return;
    editingIdRef.current = null;
    const t = localTextRef.current;
    setElements((els) => {
      if (!t.trim()) return els.filter((e) => e.id !== id);
      return els.map((e) => (e.id === id && e.type === 'text' ? { ...e, text: t } : e));
    });
    setSelectedId((prev) => (!t.trim() && prev === id ? null : prev));
    setEditingTextId(null);
  }, [setElements, setSelectedId, setEditingTextId]);

  const commitTextEditingRef = useRef(commitTextEditing);
  commitTextEditingRef.current = commitTextEditing;

  const prevToolRef = useRef(tool);
  useEffect(() => {
    const prev = prevToolRef.current;
    prevToolRef.current = tool;
    if (prev === tool || !editingTextId) return;
    const leavingTextTool = prev === 'text' && tool !== 'text';
    const enteringDrawTool = tool !== 'text' && tool !== 'select';
    if (leavingTextTool || enteringDrawTool) {
      commitTextEditingRef.current();
    }
  }, [tool, editingTextId]);

  useEffect(() => {
    if (!editingTextId) return;
    let detached = false;
    let removeListener = () => {};
    const rafId = requestAnimationFrame(() => {
      if (detached) return;
      const onDocDown = (ev) => {
        const input = textInputRef.current;
        if (input && (ev.target === input || input.contains(ev.target))) return;
        commitTextEditingRef.current();
      };
      document.addEventListener('pointerdown', onDocDown, true);
      removeListener = () => document.removeEventListener('pointerdown', onDocDown, true);
    });
    return () => {
      detached = true;
      cancelAnimationFrame(rafId);
      removeListener();
    };
  }, [editingTextId]);

  const onPointerDown = (e) => {
    if (!overlayRef.current || !image) return;
    if (tool !== 'text') {
      e.currentTarget.setPointerCapture(e.pointerId);
    }
    const p = clientToCanvas(e.clientX, e.clientY, overlayRef.current);

    if (tool === 'select') {
      const hit = hitTest(p.x, p.y, elements, selectedId);
      if (hit?.handle) {
        const el = elements.find((x) => x.id === hit.id);
        if (el) {
          const startBounds = getElementBounds(el, overlayRef.current?.getContext('2d') ?? null);
          beginGesture();
          dragRef.current = {
            mode: 'resize',
            id: hit.id,
            handle: hit.handle,
            startBounds,
            startEl: JSON.parse(JSON.stringify(el)),
            origin: { x: p.x, y: p.y },
          };
          setSelectedId(hit.id);
          return;
        }
      }
      if (hit) {
        beginGesture();
        dragRef.current = { mode: 'move', id: hit.id, last: { x: p.x, y: p.y } };
        setSelectedId(hit.id);
        setEditingTextId(null);
        return;
      }
      setSelectedId(null);
      setEditingTextId(null);
      return;
    }

    if (tool === 'text') {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      const hit = hitTest(p.x, p.y, elements, selectedId);
      const hitEl = hit ? elements.find((x) => x.id === hit.id) : null;
      if (hitEl?.type === 'text') {
        setSelectedId(hitEl.id);
        dragRef.current = { mode: 'textPending', id: hitEl.id, sx: p.x, sy: p.y };
        return;
      }
      const fs = textFontSizeForImage(image.naturalWidth);
      const id = newId();
      setLocalEditText('');
      setElements((els) => [
        ...els,
        { type: 'text', id, x: p.x, y: p.y, text: '', fontSize: fs, color },
      ]);
      setSelectedId(id);
      setEditingTextId(id);
      editingIdRef.current = id;
      return;
    }

    if (tool === 'rect') {
      dragRef.current = { mode: 'drawRect', sx: p.x, sy: p.y };
      setDraft({ type: 'rect', x: p.x, y: p.y, w: 0, h: 0, color, strokeWidth });
      return;
    }

    if (tool === 'arrow') {
      dragRef.current = { mode: 'drawArrow', sx: p.x, sy: p.y };
      setDraft({
        type: 'arrow',
        x1: p.x,
        y1: p.y,
        x2: p.x,
        y2: p.y,
        color,
        strokeWidth,
      });
      return;
    }

    if (tool === 'blur') {
      dragRef.current = { mode: 'drawBlur', sx: p.x, sy: p.y };
      setDraft({ type: 'blur', x: p.x, y: p.y, w: 0, h: 0 });
      return;
    }

    if (tool === 'crop') {
      dragRef.current = { mode: 'drawCrop', sx: p.x, sy: p.y };
      setDraft({ type: 'crop', x: p.x, y: p.y, w: 0, h: 0 });
      return;
    }

    if (tool === 'pen') {
      dragRef.current = { mode: 'pen', points: [{ x: p.x, y: p.y }] };
      setDraft({ type: 'path', points: [{ x: p.x, y: p.y }], color, strokeWidth });
    }
  };

  const onPointerMove = (e) => {
    if (!overlayRef.current || !image) return;
    const p = clientToCanvas(e.clientX, e.clientY, overlayRef.current);
    const drag = dragRef.current;
    if (!drag) return;

    if (drag.mode === 'move') {
      const dx = p.x - drag.last.x;
      const dy = p.y - drag.last.y;
      drag.last = { x: p.x, y: p.y };
      setElementsDirect((els) =>
        els.map((el) => {
          if (el.id !== drag.id) return el;
          if (el.type === 'rect' || el.type === 'blur')
            return { ...el, x: el.x + dx, y: el.y + dy };
          if (el.type === 'text') return { ...el, x: el.x + dx, y: el.y + dy };
          if (el.type === 'arrow')
            return {
              ...el,
              x1: el.x1 + dx,
              y1: el.y1 + dy,
              x2: el.x2 + dx,
              y2: el.y2 + dy,
            };
          if (el.type === 'path')
            return {
              ...el,
              points: el.points.map((pt) => ({ x: pt.x + dx, y: pt.y + dy })),
            };
          return el;
        })
      );
      return;
    }

    if (drag.mode === 'resize') {
      const dx = p.x - drag.origin.x;
      const dy = p.y - drag.origin.y;
      const nb = applyRectResize(
        drag.handle,
        drag.startBounds.x,
        drag.startBounds.y,
        drag.startBounds.w,
        drag.startBounds.h,
        dx,
        dy
      );
      const nextEl = transformElementToBounds(drag.startEl, drag.startBounds, nb);
      setElementsDirect((els) => els.map((el) => (el.id === drag.id ? nextEl : el)));
      return;
    }

    if (drag.mode === 'drawRect') {
      const r = normalizeRect(drag.sx, drag.sy, p.x, p.y);
      setDraft({ type: 'rect', ...r, color, strokeWidth });
      return;
    }

    if (drag.mode === 'drawBlur') {
      const r = normalizeRect(drag.sx, drag.sy, p.x, p.y);
      setDraft({ type: 'blur', ...r });
      return;
    }

    if (drag.mode === 'drawCrop') {
      const r = normalizeRect(drag.sx, drag.sy, p.x, p.y);
      setDraft({ type: 'crop', ...r });
      return;
    }

    if (drag.mode === 'drawArrow') {
      let x2 = p.x;
      let y2 = p.y;
      if (e.shiftKey) {
        const s = snapArrowEnd(drag.sx, drag.sy, x2, y2);
        x2 = s.x;
        y2 = s.y;
      }
      setDraft({
        type: 'arrow',
        x1: drag.sx,
        y1: drag.sy,
        x2,
        y2,
        color,
        strokeWidth,
      });
      return;
    }

    if (drag.mode === 'pen') {
      const pts = drag.points;
      const last = pts[pts.length - 1];
      if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 2) {
        pts.push({ x: p.x, y: p.y });
        setDraft({ type: 'path', points: [...pts], color, strokeWidth });
      }
      return;
    }

    if (drag.mode === 'textPending') {
      if (Math.hypot(p.x - drag.sx, p.y - drag.sy) > 6) {
        beginGesture();
        dragRef.current = { mode: 'move', id: drag.id, last: { x: p.x, y: p.y } };
        setEditingTextId(null);
      }
    }
  };

  const onPointerUp = (e) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    const drag = dragRef.current;
    const mode = drag?.mode;
    dragRef.current = null;

    if (mode === 'textPending' && drag?.id) {
      const tEl = elements.find((x) => x.id === drag.id && x.type === 'text');
      if (tEl) {
        setLocalEditText(tEl.text ?? '');
        setEditingTextId(tEl.id);
        editingIdRef.current = tEl.id;
      }
    }

    if (mode === 'move' || mode === 'resize') {
      endGesture();
    }

    const d = draftRef.current;
    if (
      d &&
      (mode === 'drawRect' ||
        mode === 'drawArrow' ||
        mode === 'drawBlur' ||
        mode === 'drawCrop' ||
        mode === 'pen')
    ) {
      commitDraft(d);
      if (mode === 'drawRect' || mode === 'drawArrow' || mode === 'pen' || mode === 'drawCrop') {
        setTool('select');
      }
    } else {
      setDraft(null);
    }
  };

  const onDoubleClick = (e) => {
    if (!overlayRef.current || tool !== 'select') return;
    const p = clientToCanvas(e.clientX, e.clientY, overlayRef.current);
    const hit = hitTest(p.x, p.y, elements, selectedId);
    if (!hit) return;
    const el = elements.find((x) => x.id === hit.id);
    if (el?.type === 'text') {
      setSelectedId(el.id);
      setEditingTextId(el.id);
      editingIdRef.current = el.id;
    }
  };

  const editingEl =
    editingTextId && elements.find((e) => e.id === editingTextId && e.type === 'text');

  useLayoutEffect(() => {
    if (!editingTextId || !textInputRef.current) return;
    const el = textInputRef.current;
    try {
      el.focus({ preventScroll: true });
    } catch {
      el.focus();
    }
    const len = el.value.length;
    if (len) el.select();
    else el.setSelectionRange(0, 0);
  }, [editingTextId]);

  let paintTextStyle = null;
  if (editingEl && overlayRef.current) {
    const canvas = overlayRef.current;
    const cw = canvas.clientWidth;
    const nw = canvas.width || 1;
    const nh = canvas.height || 1;
    const scale = cw / nw;
    const fontPx = Math.max(12, editingEl.fontSize * scale);
    const leftPct = (editingEl.x / nw) * 100;
    const topPct = (editingEl.y / nh) * 100;
    const widthPx = Math.max(140, Math.min(cw * 0.55, 560));
    paintTextStyle = {
      position: 'absolute',
      left: `${leftPct}%`,
      top: `${topPct}%`,
      width: `${widthPx}px`,
      minHeight: `${fontPx * 1.35}px`,
      zIndex: 90,
      fontSize: `${fontPx}px`,
      lineHeight: 1.25,
      fontWeight: 600,
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
      color: editingEl.color,
      background: 'rgba(255,255,255,0.5)',
      border: '1px dashed rgba(59, 130, 246, 0.85)',
      borderRadius: '4px',
      padding: '4px 6px',
      margin: 0,
      resize: 'none',
      outline: 'none',
      boxSizing: 'border-box',
      touchAction: 'auto',
      caretColor: editingEl.color,
    };
  }

  const selectedEl = selectedId ? elements.find((e) => e.id === selectedId) : null;
  let selectionChrome = null;
  if (
    tool === 'select' &&
    selectedEl &&
    !editingTextId &&
    overlayRef.current &&
    image
  ) {
    const canvas = overlayRef.current;
    const b = getElementBounds(selectedEl, canvas.getContext('2d'));
    const r = canvas.getBoundingClientRect();
    const sx = r.width / canvas.width;
    const sy = r.height / canvas.height;
    selectionChrome = {
      left: r.left + b.x * sx,
      top: r.top + b.y * sy,
      width: b.w * sx,
      height: b.h * sy,
    };
  }

  if (!image) return null;

  return (
    <Box
      position="relative"
      display="inline-block"
      lineHeight={0}
      cursor={CURSOR[tool] || 'default'}
      overflow="visible"
    >
      <Box
        position="relative"
        borderRadius="xl"
        overflow="hidden"
        boxShadow="0 12px 40px rgba(15, 23, 42, 0.1)"
        touchAction="none"
      >
        <canvas
          ref={baseRef}
          style={{
            display: 'block',
            maxWidth: '100%',
            height: 'auto',
            verticalAlign: 'top',
          }}
          aria-hidden
        />
        <canvas
          ref={overlayRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onDoubleClick={onDoubleClick}
          style={{
            position: 'absolute',
            left: 0,
            top: 0,
            width: '100%',
            height: '100%',
            touchAction: 'none',
          }}
        />
      </Box>
      {selectionChrome && (
        <HStack
          position="fixed"
          zIndex={70}
          spacing={0}
          bg="rgba(255,255,255,0.96)"
          backdropFilter="blur(8px)"
          borderRadius="lg"
          boxShadow="0 4px 18px rgba(15, 23, 42, 0.12)"
          borderWidth="1px"
          borderColor="gray.200"
          p={0.5}
          left={`${selectionChrome.left + selectionChrome.width + 6}px`}
          top={`${Math.max(8, selectionChrome.top - 4)}px`}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <Tooltip label="Bring forward" placement="top" openDelay={400}>
            <IconButton
              aria-label="Bring forward"
              icon={<MdArrowUpward size={18} />}
              size="xs"
              variant="ghost"
              borderRadius="md"
              onClick={() => onBringForward?.()}
            />
          </Tooltip>
          <Tooltip label="Send backward" placement="top" openDelay={400}>
            <IconButton
              aria-label="Send backward"
              icon={<MdArrowDownward size={18} />}
              size="xs"
              variant="ghost"
              borderRadius="md"
              onClick={() => onSendBackward?.()}
            />
          </Tooltip>
          <Tooltip label="Duplicate (⌘D)" placement="top" openDelay={400}>
            <IconButton
              aria-label="Duplicate"
              icon={<MdContentCopy size={17} />}
              size="xs"
              variant="ghost"
              borderRadius="md"
              onClick={() => onDuplicateSelected?.()}
            />
          </Tooltip>
          <Tooltip label="Delete" placement="top" openDelay={400}>
            <IconButton
              aria-label="Delete"
              icon={<MdClose size={18} />}
              size="xs"
              variant="ghost"
              colorScheme="red"
              borderRadius="md"
              onClick={() => onDeleteSelected?.()}
            />
          </Tooltip>
        </HStack>
      )}
      {editingEl && paintTextStyle && (
        <textarea
          ref={textInputRef}
          value={localEditText}
          onChange={(ev) => setLocalEditText(ev.target.value)}
          onPointerDown={(ev) => ev.stopPropagation()}
          onKeyDown={(ev) => {
            if (ev.key === 'Escape') {
              ev.preventDefault();
              commitTextEditing();
            }
          }}
          rows={Math.max(2, localEditText.split('\n').length)}
          style={paintTextStyle}
          spellCheck={false}
          autoComplete="off"
          autoCorrect="off"
          aria-label="Text annotation"
        />
      )}
    </Box>
  );
});

export default AnnotatorCanvas;
